import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/solid";
import { useAtom } from "jotai";
import { gbState } from "../../../../components/shared/GlobalState";

const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

const TaskmanagerListComponent = ({
  toggleAddTaskModal,
  setRefreshTaskList,
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(5);

  useEffect(() => {
    console.log("Current active dropdown:", activeDropdown);
  }, [activeDropdown]);

  useEffect(() => {
    console.log("Current tasks data:", data);
  }, [data]);

  const refreshTasks = useCallback(() => {
    setIsLoading(true);
    let userToken = localStorage.getItem("token");
    let urlLink = `${process.env.REACT_APP_API_URL}/task/church/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios(config)
      .then(function (response) {
        setData(response.data.tasks);
        setIsLoading(false);
      })
      .catch(function (err) {
        console.log("error api call ", err);
        setIsLoading(false);
      });
  }, [user.church_id]);

  useEffect(() => {
    setRefreshTaskList(refreshTasks);
    refreshTasks();
  }, [refreshTasks, setRefreshTaskList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCompleteTask = async (taskId) => {
    console.log("=== handleCompleteTask START ===");
    console.log("Received taskId:", taskId);
    console.log("Token:", localStorage.getItem("token"));
    const apiUrl = `${process.env.REACT_APP_API_URL}/task/${taskId}/complete`;
    console.log("API URL:", apiUrl);

    const userToken = localStorage.getItem("token");
    if (!userToken) {
      console.error("No token found");
      toast.error("Authentication error");
      return;
    }

    try {
      console.log("Making API request...");
      const response = await axios({
        method: "post",
        url: apiUrl,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      console.log("API Response:", response);

      setData((prevData) =>
        prevData.map((task) =>
          task._id === taskId ? { ...task, status: "completed" } : task
        )
      );

      toast.success("Task marked as completed");
      setActiveDropdown(null);
    } catch (err) {
      console.error("Error completing task:", err.response || err);
      toast.error(err.response?.data?.message || "Failed to complete task");
    }
    console.log("=== handleCompleteTask END ===");
  };

  const handleDeleteTask = async (taskId, e) => {
    e?.stopPropagation(); // Optional chaining for when called from modal

    if (
      !toast.custom(
        (t) => (
          <div className="p-4 bg-white rounded-lg shadow-lg">
            <p className="mb-4">Are you sure you want to delete this task?</p>
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 text-sm text-gray-600 rounded hover:bg-gray-100"
                onClick={() => toast.dismiss(t.id)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
                onClick={() => {
                  toast.dismiss(t.id);
                  return true;
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ),
        { duration: false }
      )
    )
      return;

    const userToken = localStorage.getItem("token");
    try {
      await axios({
        method: "DELETE",
        url: `${process.env.REACT_APP_API_URL}/task/delete/${taskId}`,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });

      // Update the local state to remove the deleted task
      setData(data.filter((task) => task._id !== taskId));

      // Close modal if deleting from modal view
      if (selectedTask?._id === taskId) {
        setShowModal(false);
        setSelectedTask(null);
      }
    } catch (err) {
      console.error("Error deleting task:", err);
    }
  };

  const TaskModal = ({ task, onClose }) => {
    if (!task) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="w-full max-w-lg p-8 bg-white shadow-2xl rounded-xl">
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{task.title}</h2>
            <div className="flex items-center space-x-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  task.status === "completed"
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
              </span>
            </div>
          </div>
          <div className="space-y-6">
            <p className="text-lg leading-relaxed text-gray-600">
              {task.description}
            </p>
            <div className="flex items-center space-x-6">
              <div className="flex items-center text-gray-500">
                <CalendarIcon className="w-5 h-5 mr-2 text-gray-400" />
                <span>
                  Due:{" "}
                  {new Date(task.due_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-between mt-8">
            <button
              onClick={() => handleDeleteTask(task._id)}
              className="px-6 py-2 text-red-700 transition-colors duration-200 bg-red-100 rounded-lg hover:bg-red-200"
            >
              Delete Task
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 transition-colors duration-200 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const TaskDropdown = ({ task, onClose }) => {
    console.log("TaskDropdown rendering with task:", task);

    const handleMarkComplete = async (e) => {
      console.log("Mark Complete button clicked for task:", task._id);
      e.preventDefault();
      e.stopPropagation();

      try {
        await handleCompleteTask(task._id);
        console.log("Task completion successful");
        onClose();
      } catch (error) {
        console.error("Error in handleMarkComplete:", error);
      }
    };

    return (
      <div
        className="absolute right-0 z-50 w-48 mt-1 bg-white rounded-md shadow-lg top-full ring-1 ring-black ring-opacity-5"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="py-1" role="menu">
          {task.status !== "completed" && (
            <button
              type="button"
              onClick={handleMarkComplete}
              className="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-gray-700 hover:bg-gray-100"
            >
              <CheckCircleIcon className="w-4 h-4 text-green-500" />
              <span>Mark as Complete</span>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Add edit functionality here
              onClose();
            }}
            className="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-gray-700 hover:bg-gray-100"
          >
            <PencilIcon className="w-4 h-4 text-blue-500" />
            <span>Edit Task</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTask(task._id, e);
              onClose();
            }}
            className="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-gray-700 hover:bg-gray-100"
          >
            <TrashIcon className="w-4 h-4 text-red-500" />
            <span>Delete Task</span>
          </button>
        </div>
      </div>
    );
  };

  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = data.slice(indexOfFirstTask, indexOfLastTask);
  const totalPages = Math.ceil(data.length / tasksPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-64">
        <div className="text-gray-500">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="p-1 bg-gray-50 rounded-xl">
      <div className="flex justify-end mb-4 ">
        <button
          onClick={() => toggleAddTaskModal()}
          className="flex items-center px-2 py-1 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <span className="mr-1">+</span> Task
        </button>
      </div>
      <div className="space-y-4">
        {currentTasks.map((task, index) => (
          <div
            key={task._id}
            className="transition-shadow duration-200 bg-white border border-gray-100 shadow-sm cursor-pointer rounded-xl hover:shadow-md"
            onClick={() => {
              setSelectedTask(task);
              setShowModal(true);
            }}
          >
            <div className="flex items-center justify-between p-4 group">
              <div className="flex items-center space-x-4">
                <div
                  className={`p-2 rounded-lg ${
                    task.status === "completed" ? "bg-green-50" : "bg-yellow-50"
                  }`}
                >
                  {task.status === "completed" ? (
                    <CheckCircleIcon className="w-6 h-6 text-green-500" />
                  ) : (
                    <ClockIcon className="w-6 h-6 text-yellow-500" />
                  )}
                </div>
                <div>
                  <h3
                    className="mb-1 font-medium text-gray-800 transition-colors duration-200 group-hover:text-blue-600 truncate max-w-[200px]"
                    title={task.title}
                  >
                    {truncateText(task.title, 17)}
                  </h3>
                  <div className="flex items-center text-sm text-gray-500">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {new Date(task.due_date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              </div>
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("Dropdown toggle clicked for task:", task._id);
                    setActiveDropdown(
                      activeDropdown === task._id ? null : task._id
                    );
                  }}
                  className="p-2 text-gray-400 transition-colors duration-200 rounded-lg hover:text-gray-600"
                >
                  <EllipsisVerticalIcon className="w-5 h-5" />
                </button>
                {activeDropdown === task._id && (
                  <TaskDropdown
                    task={task}
                    onClose={() => {
                      console.log("Closing dropdown");
                      setActiveDropdown(null);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {data.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500">No tasks available</p>
        </div>
      )}

      {showModal && (
        <TaskModal
          task={selectedTask}
          onClose={() => {
            setShowModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {data.length > tasksPerPage && (
        <div className="flex items-center justify-center mt-6 space-x-2">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-lg ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <div className="flex items-center space-x-2">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => paginate(index + 1)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === index + 1
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-lg ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TaskmanagerListComponent;
